import { Component, computed, inject, signal } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CombinedReportDefaults, CombinedReportSectionToggles } from '../../models/combined-report-options.model';

@Component({
  selector: 'app-combined-report-options-dialog',
  template: `
    <div class="flex flex-col">
      <p-scrollPanel [style]="{ width: '100%', height: '100%' }">
        <div class="flex flex-col space-y-3 max-h-[70vh]">

          <p class="text-sm text-gray-500 mb-2">
            Sections with a saved setup will use those settings. Others will use defaults.
          </p>

          <p-divider></p-divider>

          <!-- PERIFOR -->
          <div>
            <h3 class="underline pb-2">
              <span class="peri-txt">PERI</span><span style="color: red">FOR</span>
            </h3>
            <div class="space-y-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="toggles().includePeriforInputs" (ngModelChange)="onToggle('includePeriforInputs', $event)" [binary]="true" inputId="inclInputs" />
                <label for="inclInputs" class="ml-2">Inputs</label>
              </div>
              @if (toggles().includePeriforInputs) {
                <p class="ml-6 text-xs" [class]="hasPeriforInputsSetup ? 'text-green-600' : 'text-amber-600'">
                  {{ hasPeriforInputsSetup ? '✓ Using saved PERIFOR Inputs setup' : '⚠ No setup saved — all sections will be included' }}
                </p>
              }
            </div>
          </div>

          <p-divider></p-divider>

          <!-- PERICAL -->
          <div>
            <h3 class="underline pb-2">
              <span class="peri-txt">PERI</span><span style="color: #ff5722">CAL</span>
            </h3>
            <div class="space-y-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="toggles().includePericalOpSummary" (ngModelChange)="onToggle('includePericalOpSummary', $event)" [binary]="true" inputId="inclOpSummary" />
                <label for="inclOpSummary" class="ml-2">Operations Summary</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="toggles().includePericalResults" (ngModelChange)="onToggle('includePericalResults', $event)" [binary]="true" inputId="inclPericalResults" />
                <label for="inclPericalResults" class="ml-2">Results Report</label>
              </div>
              @if (toggles().includePericalResults) {
                <p class="ml-6 text-xs" [class]="hasPericalResultsSetup ? 'text-green-600' : 'text-amber-600'">
                  {{ hasPericalResultsSetup ? '✓ Using saved PERICAL Results setup' : '⚠ No setup saved — defaults will be used' }}
                </p>
              }
            </div>
          </div>

          <p-divider></p-divider>

          <!-- PERIVIS -->
          <div>
            <h3 class="underline pb-2">
              <span class="peri-txt">PERI</span><span style="color: #00e5ff">VIS</span>
            </h3>
            <div class="space-y-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="toggles().includePerivisLoadCaseSummary" (ngModelChange)="onToggle('includePerivisLoadCaseSummary', $event)" [binary]="true" inputId="inclLcSummary" />
                <label for="inclLcSummary" class="ml-2">Load Case Summary</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="toggles().includePerivisResults" (ngModelChange)="onToggle('includePerivisResults', $event)" [binary]="true" inputId="inclPerivisResults" />
                <label for="inclPerivisResults" class="ml-2">Results Report</label>
              </div>
              @if (toggles().includePerivisResults) {
                <p class="ml-6 text-xs" [class]="hasPerivisResultsSetup ? 'text-green-600' : 'text-amber-600'">
                  {{ hasPerivisResultsSetup ? '✓ Using saved PERIVIS Results setup' : '⚠ No setup saved — defaults will be used' }}
                </p>
              }
            </div>
          </div>

          @if (noSectionsSelected()) {
            <p-message severity="warn" text="At least one section must be selected."></p-message>
          }

        </div>
      </p-scrollPanel>

      <div class="flex justify-end gap-2 pt-4">
        <p-button label="Cancel" outlined="true" class="outlined-button" size="small" severity="danger" (onClick)="close()"></p-button>
        <p-button label="Save Setup" class="outlined-button" outlined="true" size="small" severity="success" icon="pi pi-save"
          (onClick)="save()" [disabled]="noSectionsSelected()">
        </p-button>
      </div>
    </div>
  `,
  styles: [`
    .peri-txt {
      color: #5c6bc0;
    }
  `],
  standalone: false
})
export class CombinedReportOptionsDialogComponent {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _dialogConfig = inject(DynamicDialogConfig);

  readonly toggles = signal<CombinedReportSectionToggles>({ ...CombinedReportDefaults.SectionToggles });

  readonly noSectionsSelected = computed(() => {
    const t = this.toggles();
    return !t.includePeriforInputs && !t.includePericalOpSummary &&
      !t.includePerivisLoadCaseSummary && !t.includePericalResults && !t.includePerivisResults;
  });

  readonly hasPeriforInputsSetup: boolean;
  readonly hasPericalResultsSetup: boolean;
  readonly hasPerivisResultsSetup: boolean;

  constructor() {
    const data = this._dialogConfig.data;
    this.hasPeriforInputsSetup  = !!data?.hasPeriforInputsSetup;
    this.hasPericalResultsSetup = !!data?.hasPericalResultsSetup;
    this.hasPerivisResultsSetup = !!data?.hasPerivisResultsSetup;

    const initial = data?.initialToggles as CombinedReportSectionToggles | null | undefined;
    if (initial) {
      this.toggles.set({ ...initial });
    }
  }

  onToggle(field: keyof CombinedReportSectionToggles, value: boolean): void {
    this.toggles.update(t => ({ ...t, [field]: value }));
  }

  save(): void {
    this._dialogRef.close(this.toggles());
  }

  close(): void {
    this._dialogRef.close(null);
  }
}
